/* platform/PlatformAnalytics — Historical reporting. No CRUD, no actionable items.
   Filters: Today / 7d / 30d / Quarter / Year / Custom.
   Operational items (pending orgs, expiring licenses, etc.) live in Dashboard. */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Pill } from '../shared/ui';
import { StatCard, PageHead, MiniBar, Th } from './_ui';
import { pct, PLAN_TONES } from './platformStore';
import { getOverview } from '../api/platform.api.js';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { exportCSV, exportExcel, COLS } from './utils/exportUtils.js';

/* ── Date range helpers ──────────────────────────────────────── */
const today  = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const daysAgo = (n) => { const d = today(); d.setDate(d.getDate() - n); return d; };

const FILTERS = [
  { id: 'today',   label: 'Today'       },
  { id: '7d',      label: 'Last 7 Days' },
  { id: '30d',     label: 'Last 30 Days'},
  { id: 'quarter', label: 'Last Quarter'},
  { id: 'year',    label: 'This Year'   },
  { id: 'all',     label: 'All Time'    },
];

const rangeStart = (filterId, custom) => {
  if (filterId === 'custom' && custom?.from) return new Date(custom.from);
  if (filterId === 'today')   return today();
  if (filterId === '7d')      return daysAgo(7);
  if (filterId === '30d')     return daysAgo(30);
  if (filterId === 'quarter') return daysAgo(90);
  if (filterId === 'year')    { const d = new Date(); d.setMonth(0, 1); d.setHours(0,0,0,0); return d; }
  return new Date(0);
};

const inRange = (dateStr, from, to) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d >= from && d <= to;
};

const buildMonthlyBuckets = (items, dateKey, n = 12) => {
  const buckets = Array.from({ length: n }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (n - 1 - i)); d.setHours(0,0,0,0);
    return { label: d.toLocaleDateString('en-IN', { month: 'short' }), start: new Date(d), count: 0 };
  });
  buckets.forEach((b, i) => {
    const end = i < buckets.length - 1 ? buckets[i + 1].start : new Date(8640000000000000);
    items.forEach(x => {
      const d = x[dateKey] ? new Date(x[dateKey]) : null;
      if (d && d >= b.start && d < end) b.count++;
    });
  });
  return buckets;
};

/* ── Placeholder card ────────────────────────────────────────── */
const PlaceholderCard = ({ title, icon, desc }) => (
  <div className="nx-cc-chart-card" style={{ opacity: 0.65 }}>
    <div className="nx-cc-chart-head">
      <span className="nx-cc-chart-title">{title}</span>
      <Pill tone="gray">Coming Soon</Pill>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 10 }}>
      <NexusIcon name={icon} size={28} style={{ color: 'var(--nx-text-faint, #CBD5E1)' }} />
      <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', textAlign: 'center' }}>{desc}</span>
    </div>
  </div>
);

export default function PlatformAnalytics({ data }) {
  const navigate = useNavigate();
  const { organizations, licenses, workspaces, members } = data.state;

  const [filterId, setFilterId] = useState('30d');
  const [custom,   setCustom]   = useState({ from: '', to: '' });
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    getOverview().then(setOverview).catch(() => {});
  }, []);

  /* ── Filter state ───────────────────────────────────────────── */
  const now   = useMemo(() => new Date(), []);
  const from  = useMemo(() => rangeStart(filterId, custom), [filterId, custom]);
  const to    = now;

  /* ── Filtered subsets ───────────────────────────────────────── */
  const filteredOrgs    = useMemo(() => organizations.filter(o => inRange(o.created, from, to)),  [organizations, from, to]);
  const filteredMembers = useMemo(() => members.filter(m       => inRange(m.created, from, to)), [members, from, to]);

  /* ── Monthly buckets (always 12 months, filter-independent for trend context) */
  const orgBuckets     = useMemo(() => buildMonthlyBuckets(organizations, 'created'), [organizations]);
  const memberBuckets  = useMemo(() => buildMonthlyBuckets(members,       'created'), [members]);
  const wsBuckets      = useMemo(() => buildMonthlyBuckets(workspaces,    'created'), [workspaces]);

  /* ── Platform-wide totals (use live overview or derive from store) ── */
  const totalOrgs    = overview?.totalOrgs      ?? organizations.length;
  const totalUsers   = overview?.totalUsers     ?? organizations.reduce((s, o) => s + (o.users || 0), 0);
  const totalWs      = overview?.totalWorkspaces ?? workspaces.length;
  const userLicenses = licenses.filter(l => l.type === 'USER');
  const totalLic     = overview?.totalLicensesAllotted ?? userLicenses.reduce((s, l) => s + (l.purchased ?? 0), 0);
  const usedLic      = overview?.totalLicensesUsed     ?? organizations.reduce((s, o) => s + (o.users || 0), 0);
  const licPct       = overview?.licenseUtilizationPct ?? pct(usedLic, totalLic);

  /* ── Status breakdown ────────────────────────────────────────── */
  const statusGroups = [
    { label: 'Active',    color: '#0E8C5A', count: organizations.filter(o => o.status === 'ACTIVE').length            },
    { label: 'Pending',   color: '#E08A00', count: organizations.filter(o => o.status === 'ORG_ADMIN_PENDING').length  },
    { label: 'Draft',     color: '#6E3FBA', count: organizations.filter(o => o.status === 'DRAFT').length             },
    { label: 'Suspended', color: '#C26A1F', count: organizations.filter(o => o.status === 'SUSPENDED').length         },
    { label: 'Archived',  color: '#64748B', count: organizations.filter(o => o.status === 'ARCHIVED').length          },
  ];

  /* ── Plan distribution (dynamic — derived from actual org data) ── */
  const planGroups = useMemo(() => {
    const map = {};
    organizations.forEach(o => { if (o.plan) map[o.plan] = (map[o.plan] || 0) + 1; });
    return Object.entries(map)
      .map(([plan, count]) => ({ label: plan, count, tone: PLAN_TONES[plan] || 'gray' }))
      .sort((a, b) => b.count - a.count);
  }, [organizations]);

  /* ── Top orgs by users ──────────────────────────────────────── */
  const topOrgs = useMemo(() =>
    [...organizations].sort((a, b) => (b.users || 0) - (a.users || 0)).slice(0, 10),
  [organizations]);

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Platform Analytics"
        sub="Historical reporting — operational items are in Dashboard"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="RefreshCw" onClick={() => { data.reload(); getOverview().then(setOverview).catch(() => {}); }}>Refresh</Btn>
            <Btn variant="ghost" icon="Download" onClick={() => exportCSV('platform-analytics', organizations, COLS.organizations)}>CSV</Btn>
            <Btn variant="ghost" icon="FileSpreadsheet" onClick={() => exportExcel('platform-analytics', [{ name: 'Organizations', rows: organizations, columns: COLS.organizations }])}>Excel</Btn>
          </div>
        }
      />

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button key={f.id}
            onClick={() => setFilterId(f.id)}
            style={{
              padding: '5px 12px', borderRadius: 20, border: '1px solid',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: filterId === f.id ? '#125AB0' : 'var(--nx-surface)',
              color:      filterId === f.id ? '#fff'    : 'var(--nx-text-muted)',
              borderColor: filterId === f.id ? '#125AB0' : 'var(--nx-border)',
              transition: 'all 0.15s',
            }}>
            {f.label}
          </button>
        ))}
        <button
          onClick={() => setFilterId('custom')}
          style={{
            padding: '5px 12px', borderRadius: 20, border: '1px solid',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: filterId === 'custom' ? '#125AB0' : 'var(--nx-surface)',
            color:      filterId === 'custom' ? '#fff'    : 'var(--nx-text-muted)',
            borderColor: filterId === 'custom' ? '#125AB0' : 'var(--nx-border)',
          }}>
          Custom
        </button>
        {filterId === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <input type="date" className="nx-input" style={{ width: 140, fontSize: 12 }}
              value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} />
            <span className="nx-muted nx-mini">to</span>
            <input type="date" className="nx-input" style={{ width: 140, fontSize: 12 }}
              value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} />
          </div>
        )}
        <span className="nx-muted nx-mini" style={{ marginLeft: 8 }}>
          {filteredOrgs.length} org{filteredOrgs.length !== 1 ? 's' : ''} in period
        </span>
      </div>

      {/* ── Platform totals ─────────────────────────────────────── */}
      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <StatCard label="Total Organizations" value={totalOrgs}              icon="Building2"   color="#125AB0" onClick={() => navigate('/platform/organizations')} />
        <StatCard label="Total Users"         value={totalUsers.toLocaleString()} icon="Users"  color="#0E8C5A" />
        <StatCard label="Total Workspaces"    value={totalWs.toLocaleString()}    icon="LayoutGrid" color="#A8508A" />
        <StatCard label="Total Licenses"      value={totalLic.toLocaleString()}   icon="Key"    color="#6E3FBA" />
        <StatCard label="License Utilization" value={licPct + '%'} sub={usedLic + ' / ' + totalLic + ' seats'} icon="Activity" color={licPct > 85 ? '#E08A00' : '#0E8C5A'} />
      </div>

      {/* ── Status breakdown ─────────────────────────────────────── */}
      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginTop: 12 }}>
        {statusGroups.map(g => (
          <StatCard key={g.label} label={g.label} value={g.count} icon="Activity" color={g.color}
            onClick={() => navigate('/platform/organizations')} />
        ))}
      </div>

      {/* ── Growth charts ────────────────────────────────────────── */}
      <div className="nx-cc-grid2 nx-analytics-grid" style={{ marginTop: 20 }}>

        <div className="nx-cc-chart-card nx-analytics-card">
          <div className="nx-cc-chart-head nx-analytics-card-header">
            <span className="nx-cc-chart-title">Organization Growth</span>
            <Pill tone="blue">{organizations.length} total</Pill>
          </div>
          <div className="nx-analytics-card-body">
            <div className="nx-analytics-card-viz">
              <MiniBar points={orgBuckets.map(b => b.count)} color="#125AB0"
                chartTitle="Organization Growth"
                labels={orgBuckets.map(b => b.label)}
                label={orgBuckets[0]?.label + ' – ' + orgBuckets[orgBuckets.length - 1]?.label + ' (12 months)'}
                current={filteredOrgs.length + ' in period'} />
            </div>
            <div className="nx-analytics-card-footer">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {orgBuckets.slice(-6).map((b, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{b.count}</span>
                    <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="nx-cc-chart-card nx-analytics-card">
          <div className="nx-cc-chart-head nx-analytics-card-header">
            <span className="nx-cc-chart-title">User Growth</span>
            <Pill tone="green">{totalUsers.toLocaleString()} total</Pill>
          </div>
          <div className="nx-analytics-card-body">
            <div className="nx-analytics-card-viz">
              <MiniBar points={memberBuckets.map(b => b.count)} color="#0E8C5A"
                chartTitle="User Growth"
                labels={memberBuckets.map(b => b.label)}
                label="New users per month (12 months)"
                current={filteredMembers.length + ' in period'} />
            </div>
            <div className="nx-analytics-card-footer">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {memberBuckets.slice(-6).map((b, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{b.count}</span>
                    <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="nx-cc-chart-card nx-analytics-card">
          <div className="nx-cc-chart-head nx-analytics-card-header">
            <span className="nx-cc-chart-title">Workspace Growth</span>
            <Pill tone="purple">{totalWs.toLocaleString()} total</Pill>
          </div>
          <div className="nx-analytics-card-body">
            <div className="nx-analytics-card-viz">
              <MiniBar points={wsBuckets.map(b => b.count)} color="#A8508A"
                chartTitle="Workspace Growth"
                labels={wsBuckets.map(b => b.label)}
                label="New workspaces per month (12 months)"
                current={wsBuckets.reduce((s, b) => s + b.count, 0) + ' tracked'} />
            </div>
            <div className="nx-analytics-card-footer">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {wsBuckets.slice(-6).map((b, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{b.count}</span>
                    <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="nx-cc-chart-card nx-analytics-card">
          <div className="nx-cc-chart-head nx-analytics-card-header">
            <span className="nx-cc-chart-title">License Utilization</span>
            <Pill tone={licPct > 85 ? 'amber' : 'green'}>{licPct}% utilized</Pill>
          </div>
          <div className="nx-analytics-card-body">
            <div className="nx-analytics-card-viz is-list">
            {licenses.length === 0
              ? <div style={{ padding: '20px 0', fontSize: 12, color: 'var(--nx-text-muted)' }}>No licenses assigned.</div>
              : <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {licenses.map(l => {
                    const effectiveUsed = l.type === 'USER'
                      ? (organizations.find(o => o.id === l.org)?.users || 0)
                      : (l.used || 0);
                    const p       = pct(effectiveUsed, l.purchased);
                    const orgName = organizations.find(o => o.id === l.org)?.name || '—';
                    return (
                      <div key={l.id}
                        title={`${orgName} · ${l.type} license · ${effectiveUsed.toLocaleString()} / ${(l.purchased || 0).toLocaleString()} seats · ${p}% utilized`}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--nx-divider, #F1F5F9)', cursor: 'pointer' }}
                        onClick={() => navigate('/platform/organizations/' + l.org)}>
                        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{orgName}</span>
                        <Pill tone={l.type === 'USER' ? 'blue' : l.type === 'BOT' ? 'purple' : 'green'}>{({ USER: 'User', BOT: 'Bot', AGENT: 'Agent', CUSTOM: 'Custom' })[l.type] || l.type}</Pill>
                        <div className="nx-cc-lic-track" style={{ flex: 1 }}>
                          <span style={{ width: p + '%', background: p > 90 ? 'var(--nx-danger)' : p > 75 ? '#E08A00' : 'var(--nx-success)' }} />
                        </div>
                        <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', width: 32, textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>{p}%</span>
                      </div>
                    );
                  })}
                </div>
            }
            </div>
          </div>
        </div>

      </div>

      {/* ── Plan distribution + Onboarding ───────────────────────── */}
      <div className="nx-cc-grid2 nx-analytics-grid" style={{ marginTop: 16 }}>

        <div className="nx-cc-chart-card nx-analytics-card">
          <div className="nx-cc-chart-head nx-analytics-card-header">
            <span className="nx-cc-chart-title">Plan Distribution</span>
            <Pill tone="blue">{organizations.length} orgs</Pill>
          </div>
          <div className="nx-analytics-card-body">
            <div className="nx-analytics-card-viz is-list">
              {planGroups.length === 0
                ? <div style={{ padding: '20px 0', fontSize: 12, color: 'var(--nx-text-muted)' }}>No organizations.</div>
                : <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {planGroups.map(p => {
                      const share = pct(p.count, organizations.length);
                      return (
                        <div key={p.label} title={`${p.label}: ${p.count} org${p.count !== 1 ? 's' : ''} · ${share}% of total`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--nx-divider, #F1F5F9)' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, width: 84, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</span>
                          <div className="nx-cc-lic-track" style={{ flex: 1 }}>
                            <span style={{ width: share + '%', background: '#125AB0' }} />
                          </div>
                          <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', width: 58, textAlign: 'right', flexShrink: 0 }}>
                            <span style={{ fontWeight: 700, color: 'var(--nx-text)' }}>{p.count}</span>
                            <span style={{ color: 'var(--nx-text-muted)', marginLeft: 3 }}>({share}%)</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
              }
            </div>
          </div>
        </div>

        <div className="nx-cc-chart-card nx-analytics-card">
          <div className="nx-cc-chart-head nx-analytics-card-header">
            <span className="nx-cc-chart-title">Monthly Onboarding</span>
            <Pill tone="blue">12-month view</Pill>
          </div>
          <div className="nx-analytics-card-body">
            <div className="nx-analytics-card-viz is-list">
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table className="nx-table">
                  <thead><tr><Th icon="Calendar">Month</Th><Th icon="Building2">Orgs</Th><Th icon="Users">Users</Th><Th icon="LayoutGrid">Workspaces</Th></tr></thead>
                  <tbody>{orgBuckets.slice().reverse().slice(0, 6).map((b, i) => (
                    <tr key={i} className="nx-tr">
                      <td><span className="nx-muted">{b.label}</span></td>
                      <td><span className="nx-cell is-mono">{b.count}</span></td>
                      <td><span className="nx-cell is-mono">{memberBuckets.slice().reverse()[i]?.count ?? 0}</span></td>
                      <td><span className="nx-cell is-mono">{wsBuckets.slice().reverse()[i]?.count ?? 0}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Top Organizations by Users ───────────────────────────── */}
      {topOrgs.length > 0 && (
        <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Top Organizations by Users</span>
            <Pill tone="blue">{Math.min(10, organizations.length)} shown</Pill>
          </div>
          <div className="nx-table-wrap" style={{ marginTop: 10 }}><div className="nx-table-scroll"><table className="nx-table">
            <thead><tr>
              <Th icon="Building2">Organization</Th>
              <Th icon="Tag">Plan</Th>
              <Th icon="Users">Users</Th>
              <Th icon="Activity">Status</Th>
            </tr></thead>
            <tbody>{topOrgs.map((o, i) => (
              <tr key={o.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => navigate('/platform/organizations/' + o.id)}>
                <td>
                  <span className="nx-row-name">
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', width: 18, textAlign: 'right', marginRight: 4 }}>#{i + 1}</span>
                    <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 10 }}>{o.name[0]}</span>
                    <span className="nx-row-name-text">{o.name}</span>
                  </span>
                </td>
                <td><Pill tone={PLAN_TONES[o.plan] || 'gray'}>{o.plan}</Pill></td>
                <td><span className="nx-cell is-mono">{(o.users || 0).toLocaleString()}</span></td>
                <td><span className="nx-muted nx-mini">{o.status}</span></td>
              </tr>
            ))}</tbody>
          </table></div></div>
        </div>
      )}

      {/* ── Placeholders ─────────────────────────────────────────── */}
      <div className="nx-cc-grid2" style={{ marginTop: 16 }}>
        <PlaceholderCard
          title="AI Agent Growth"
          icon="Cpu"
          desc="Agent deployment trends across all organizations will appear here once agent telemetry is connected." />
        <PlaceholderCard
          title="Storage Usage"
          icon="Database"
          desc="Document and media storage breakdown per organization will appear here once the storage service is instrumented." />
      </div>
    </div>
  );
}
